import React from 'react';
import { Dialog } from '@headlessui/react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useCheckoutDotComFlowCard } from 'views/Accounts/hooks/useCheckoutDotComFlowCard';
import { Country, getISO2Country } from 'utils';
import { AuthUserQuery } from '__generated__/graphql';
import AddCardModalFlowForm from './AddCardModalFlowForm';
import { GET_AUTH_USER } from '../../../graphql/getAuthUser';

interface AddCardModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * checkout.com FULL FLOW variant of the add-card modal. The Flow SDK submits the (zero-value) charge
 * against the backend payment session — there is no `createCardPayment` call. Two-phase: the user
 * confirms their name (phase 1) which creates the session with the final values, then the SDK card
 * fields mount and the form's Save button drives `submit()` (phase 2). On approval the hook finalizes
 * and navigates to `#success`; the 3DS redirect is finalized by `useFinalizeOnPaymentRedirect`.
 */
export default function AddCardModalFlow({ open, onClose }: AddCardModalProps) {
  const { t } = useTranslation();
  const [makeCardDefault, setMakeCardDefault] = React.useState(true);
  const [nameOnCard, setNameOnCard] = React.useState<string>('');
  const [country, setCountry] = React.useState<Country>('United Kingdom');
  const [detailsConfirmed, setDetailsConfirmed] = React.useState(false);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  const { data: authUserData } = useQuery<AuthUserQuery>(GET_AUTH_USER);
  const customerName = authUserData?.authUser?.fullName || undefined;
  const customerEmail = authUserData?.authUser?.emailAddress || undefined;

  const baseReturnUri = window.location.origin + window.location.pathname;
  const successUrl = `${baseReturnUri}#success`;
  const failureUrl = `${baseReturnUri}#failure`;

  const {
    containerRef,
    submit,
    isCardComplete,
    ready: flowReady,
    error: flowError,
  } = useCheckoutDotComFlowCard({
    componentType: 'card',
    amount: 0,
    successUrl,
    failureUrl,
    customerName,
    customerEmail,
    billingCountry: getISO2Country(country),
    clientName: nameOnCard,
    storeCardFlag: true,
    mitConsentedFlag: true, // consent to store ⇒ opting into MIT (confirmed by HHW).
    defaultMitFlag: makeCardDefault,
    enabled: open && detailsConfirmed,
    onError: () => {
      setError(new Error('Card payment failed'));
      setIsProcessing(false);
    },
  });

  const handleContinue = () => setDetailsConfirmed(true);
  const handleSave = () => {
    setIsProcessing(true);
    submit();
  };

  const canContinue = nameOnCard.length >= 5 && country.length > 0 && !!customerName && !!customerEmail;
  const payDisabled = isProcessing || !flowReady || !isCardComplete;

  return (
    <Dialog as="div" className="relative z-50" open={open} onClose={onClose}>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm" aria-hidden="true" />

      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          <AddCardModalFlowForm
            nameOnCard={nameOnCard}
            country={country}
            onCountryChanged={setCountry}
            onNameOnCardChanged={setNameOnCard}
            makeCardDefault={makeCardDefault}
            onMakeCardDefaultChanged={setMakeCardDefault}
            detailsConfirmed={detailsConfirmed}
            canContinue={canContinue}
            onContinue={handleContinue}
            onSaveCardButtonClicked={handleSave}
            payDisabled={payDisabled}
            error={error}
            onClose={onClose}
            isProcessing={isProcessing}
            containerRef={containerRef}
            flowReady={flowReady}
            flowError={flowError}
          />
        </div>
      </div>
    </Dialog>
  );
}
